import {
  Component,
  Input,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  inject,
  OnDestroy
} from '@angular/core'
import { EditComponent } from '../../../../components/edit-components/edit-component'
import { ConfirmationService } from 'primeng/api'
import jspreadsheet from 'jspreadsheet-ce'
import { GridColumn } from '../../models/wizard-data'

@Component({
  selector: 'jml-lib-grid-input',
  templateUrl: './grid-input.component.html',
  styleUrls: ['./grid-input.component.scss']
})
export class GridInputComponent extends EditComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() columns: GridColumn[] = []
  @Input() value: any[][] = []
  @Input() options: any = {}

  @ViewChild('spreadsheet') spreadsheetEl: ElementRef<HTMLDivElement>

  private spreadsheetInstance: any = null
  private confirmationService = inject(ConfirmationService)
  private keydownHandler: (e: KeyboardEvent) => void

  ngAfterViewInit(): void {
    if (!this.spreadsheetEl?.nativeElement) {
      return
    }

    const jColumns = this.mapColumnsToJspreadsheet(this.columns)
    const initialData = this.value
    const options = this.options

    const instance = jspreadsheet(this.spreadsheetEl.nativeElement, {
      contextMenu: function (): any {
        return false
      },
      worksheets: [
        {
          data: initialData,
          columns: jColumns,
          minDimensions: [this.columns.length, 1],
          allowInsertColumn: false,
          allowInsertRow: false,
          allowDeleteColumn: false,
          ...options
        }
      ],
      onchange: () => this.syncData(),
      oninsertrow: () => this.syncData()
    })

    this.spreadsheetInstance = instance[0]

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        if (this.isRowSelectionMode()) {
          e.preventDefault()
          e.stopPropagation()
          this.handleDeleteRequest()
        }
      }
    }

    this.spreadsheetEl.nativeElement.addEventListener('keydown', this.keydownHandler, true)
  }

  ngOnDestroy(): void {
    if (this.keydownHandler && this.spreadsheetEl?.nativeElement) {
      this.spreadsheetEl.nativeElement.removeEventListener('keydown', this.keydownHandler, true)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange && this.spreadsheetInstance) {
      const newData = changes['value'].currentValue

      if (newData?.length) {
        this.spreadsheetInstance.setData(newData)
      }
    }
  }

  private isRowSelectionMode(): boolean {
    const selection = this.spreadsheetInstance.selectedCell

    if (!selection) {
      return false
    }

    const [x1, y1] = selection

    return (Number.isInteger(x1) && Number.isInteger(y1)) || Number.isInteger(y1)
  }

  private handleDeleteRequest(): void {
    const selectedRows = this.spreadsheetInstance.getSelectedRows()

    if (selectedRows?.length > 0) {
      this.confirmDeleteRows(selectedRows)
    }
  }

  private confirmDeleteRows(rowNumbers: number[]): void {
    const isSingle = rowNumbers.length === 1
    const message = isSingle
      ? 'Sind Sie sicher, dass Sie diese Zeile löschen wollen?'
      : `Sind Sie sicher, dass Sie ${rowNumbers.length} Zeilen löschen wollen?`

    this.confirmationService.confirm({
      message,
      header: isSingle ? 'Zeile löschen' : 'Zeilen löschen',
      key: 'global',
      accept: () => {
        const sortedRows = [...rowNumbers].sort((a, b) => b - a)
        sortedRows.forEach((row) => this.spreadsheetInstance.deleteRow(row))
        this.syncData()
      }
    })
  }

  private mapColumnsToJspreadsheet(columns: GridColumn[]): any[] {
    return columns.map((col) => {
      const baseColumn = {
        title: col.header,
        width: col?.width ?? 120
      }

      switch (col.type) {
        case 'String':
          return { ...baseColumn, type: 'text' }
        case 'int':
          return { ...baseColumn, type: 'numeric' }
        case 'float':
          return { ...baseColumn, type: 'numeric' }
        case 'boolean':
          return { ...baseColumn, type: 'checkbox' }
        case 'date':
          return { ...baseColumn, type: 'calendar', options: { format: 'DD.MM.YYYY HH:MI' } }
        case 'testType':
          return { ...baseColumn, type: this.createButtonEditor(), readOnly: true }
        default:
          return { ...baseColumn, type: 'text' }
      }
    })
  }

  private createButtonEditor(): any {
    return {
      createCell: (cell: HTMLElement, value: string, x: number, y: number) => {
        this.renderButtons(cell, value, x, y)
      },
      updateCell: (cell: HTMLElement, value: string, x: number, y: number) => {
        this.renderButtons(cell, value, x, y)
        return value
      },
      openEditor: () => false,
      closeEditor: () => null
    }
  }

  private renderButtons(cell: HTMLElement, value: string, x: number, y: number): void {
    cell.innerHTML = ''
    cell.style.textAlign = 'center'

    const container = document.createElement('div')
    container.style.display = 'flex'
    container.style.gap = '4px'
    container.style.justifyContent = 'center'

    for (let i = 1; i <= 3; i++) {
      const btn = document.createElement('button')
      btn.textContent = String(i)
      btn.style.cssText = 'padding: 2px 8px; cursor: pointer; border: 1px solid #ccc; border-radius: 3px;'
      btn.onclick = (e) => {
        e.stopPropagation()
        console.log('Button clicked:', { button: i, cellValue: value, x, y })
      }
      container.appendChild(btn)
    }

    cell.appendChild(container)
  }

  private syncData(): void {
    if (this.control) {
      this.emitUpdate()
    }
  }
}
